/**
 * MuiTasksList - Optimized Production Tasks List Page
 *
 * OPTIMIZATION: Phase 8.1 - Data fetching optimization
 *
 * Changes made:
 * - Replaced Redux dispatch(onGetTasks()) with direct API call fetchTasksForGrouping()
 * - fetchTasksForGrouping() fetches only required fields for grouping/display
 * - Added debounced search (300ms)
 * - Added refresh button and better loading states
 *
 * Note: This is a dashboard-like page that groups tasks by order reference.
 * FlexFlow doesn't support GROUP BY, so client-side grouping is necessary.
 * Full data loading is required for correct grouping/aggregation.
 *
 * Benefits:
 * - Reduced data transfer by fetching only essential fields
 * - Direct API call instead of Redux for simpler data flow
 * - Better error handling and loading states
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import PropTypes from "prop-types"
import {
  Box,
  Container,
  TextField,
  InputAdornment,
  Button,
  Typography,
  IconButton,
  Grid,
  Avatar,
  ImageList,
  ImageListItem,
  Chip,
  Slide,
  Divider,
  CircularProgress,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import SearchIcon from "@mui/icons-material/Search"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import VisibilityIcon from "@mui/icons-material/Visibility"
import CloseIcon from "@mui/icons-material/Close"
import AssignmentIcon from "@mui/icons-material/Assignment"
import FactoryIcon from "@mui/icons-material/Factory"
import RefreshIcon from "@mui/icons-material/Refresh"

// MUI Components
import MuiPageHeader from "../../components/mui/MuiPageHeader"
import MuiCard from "../../components/mui/MuiCard"
import MuiStatusChip from "../../components/mui/MuiStatusChip"

// API - using optimized fetchTasksForGrouping
import { fetchTasksForGrouping, getCustomers } from "helpers/api_helper"

/**
 * MuiTasksList - Production Tasks List with DataGrid
 *
 * UX Laws Applied:
 * - Miller's Law: Shows 6 key columns (row #, Order ID, Customer, Label, Date, Status)
 * - Law of Proximity: Groups task details in expandable rows
 * - Von Restorff Effect: Status badges with distinct colors for quick scanning
 * - Jakob's Law: Familiar table pattern with sortable columns and search
 * - Fitts's Law: Large click targets for row expansion
 */
const MuiTasksList = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [customersMap, setCustomersMap] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedOrder, setSelectedOrder] = useState(null)
  const searchTimerRef = useRef(null)

  document.title = "Task List | FlexFlow ERP"

  // ============================================================================
  // FETCH TASKS - Using optimized fetchTasksForGrouping
  // Only fetches required fields for grouping/display
  // ============================================================================
  const fetchTasks = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchTasksForGrouping()
      setTasks(res || [])
    } catch (err) {
      console.error("Error fetching tasks:", err)
      setError(err.message || "Failed to load tasks")
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch tasks on mount and when location changes
  useEffect(() => {
    fetchTasks()
  }, [location.key, fetchTasks])

  // Fetch customers for label lookup
  useEffect(() => {
    (async () => {
      try {
        const customers = await getCustomers()
        const map = {}
        customers.forEach((c) => {
          map[c.id] = c
        })
        setCustomersMap(map)
      } catch (e) {
        console.error("Failed to load customers", e)
      }
    })()
  }, [])

  // Debounced search (300ms)
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [searchTerm])

  // Refresh handler
  const handleRefresh = useCallback(() => {
    fetchTasks()
  }, [fetchTasks])

  // Group tasks by order
  const createOrderTaskObjects = (payload) => {
    const orders = {};
    payload.forEach((task) => {
      const { orderref, name, id, type, status, photos, seq, quantity, order } = task;
      const customer_id = order?.customer_id;
      const customer_name = order?.customer_name ?? task.customer_name;
      const expectedDate = order?.expectedDate ?? task.expectedDate;
      if (!orders[orderref]) {
        orders[orderref] = { orderref, customer_id, customer_name, expectedDate, tasks: [] };
      }
      orders[orderref].tasks.push({ name, id, type, status, photos, seq, quantity });
    });

    return Object.values(orders).map((o) => ({
      ...o,
      tasks: o.tasks.sort((a, b) => a.seq - b.seq),
    }));
  };

  // Build base orders with derived status
  const baseOrders = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    return createOrderTaskObjects(tasks).map((o, index) => {
      const statuses = o.tasks.map((t) => t.status);
      const status =
        statuses.includes("PENDING")
          ? "PENDING"
          : statuses.includes("PRODUCTION")
          ? "PRODUCTION"
          : statuses.includes("STARTED")
          ? "STARTED"
          : statuses.includes("DELIVERY")
          ? "DELIVERY"
          : statuses.length && statuses.every((s) => s === "FINISHED")
          ? "FINISHED"
          : statuses[0] || "-";
      return { ...o, status, id: o.orderref || index };
    });
  }, [tasks]);

  // Helper for customer label lookup
  const getCustomerLabel = (customer_id) =>
    customer_id ? customersMap[customer_id]?.label : undefined ?? "";

  // Filter orders based on debounced search term
  const filteredOrders = useMemo(() => {
    if (!debouncedSearch) return baseOrders
    const term = debouncedSearch.toLowerCase()
    return baseOrders.filter((o) => {
      const searchableText = [
        String(o.orderref ?? ""),
        String(o.customer_name ?? ""),
        String(getCustomerLabel(o.customer_id) ?? ""),
        String(o.expectedDate ?? ""),
        String(o.status ?? ""),
      ]
        .join(" ")
        .toLowerCase()
      return searchableText.includes(term)
    })
  }, [baseOrders, debouncedSearch, customersMap])

  // Parse JSON photos
  const parseJson = (array) => {
    try {
      return JSON.parse(array);
    } catch {
      return [];
    }
  };

  // Map status to chip color
  const getStatusColor = (status) => {
    const statusMap = {
      PENDING: "pending",
      STARTED: "inprogress",
      PRODUCTION: "processing",
      DELIVERY: "shipped",
      FINISHED: "completed",
    };
    return statusMap[status] || "default";
  };

  // DataGrid columns - Miller's Law: 6 key columns
  const columns = useMemo(
    () => [
      {
        field: "orderref",
        headerName: "Order ID",
        width: 130,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            #{params.value}
          </Typography>
        ),
      },
      {
        field: "customer_name",
        headerName: "Customer",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "customer_label",
        headerName: "Customer Label",
        width: 150,
        valueGetter: (value, row) => getCustomerLabel(row.customer_id) || "-",
      },
      {
        field: "expectedDate",
        headerName: "Expected Date",
        width: 130,
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => (
          <MuiStatusChip
            status={getStatusColor(params.value)}
            label={params.value}
            size="small"
            variant="filled"
          />
        ),
      },
      {
        field: "tasks_count",
        headerName: "Tasks",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.tasks?.length || 0}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedOrder(selectedOrder?.id === params.row.id ? null : params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [selectedOrder, customersMap]
  );

  // Render task details panel
  const renderTaskDetailsPanel = () => {
    if (!selectedOrder || !selectedOrder.tasks || selectedOrder.tasks.length === 0) {
      return null;
    }

    return (
      <Slide direction="up" in={Boolean(selectedOrder)} mountOnEnter unmountOnExit>
        <MuiCard sx={{ mt: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                <FactoryIcon />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  Order #{selectedOrder.orderref}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedOrder.customer_name} • {selectedOrder.tasks.length} tasks
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setSelectedOrder(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Tasks Grid */}
          <Grid container spacing={2}>
            {selectedOrder.tasks.map((tsk, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <MuiCard
                  variant="outlined"
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    bgcolor:
                      tsk.status === "PENDING"
                        ? "error.50"
                        : tsk.status === "FINISHED"
                        ? "success.50"
                        : "background.paper",
                    "&:hover": { boxShadow: 3, transform: "translateY(-2px)" },
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => navigate("/single/" + tsk.type)}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor:
                          tsk.status === "FINISHED"
                            ? "success.main"
                            : tsk.status === "PENDING"
                            ? "error.main"
                            : "primary.main",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <AssignmentIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {tsk.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {tsk.id} • {tsk.type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Qty: {tsk.quantity} • Seq: {tsk.seq}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <MuiStatusChip
                          status={getStatusColor(tsk.status)}
                          label={tsk.status}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Box>
                  {/* Task Photos */}
                  {tsk.photos && parseJson(tsk.photos).length > 0 && (
                    <ImageList
                      sx={{ mt: 1.5, maxHeight: 80 }}
                      cols={Math.min(parseJson(tsk.photos).length, 3)}
                      rowHeight={70}
                    >
                      {parseJson(tsk.photos).slice(0, 3).map((pht, idx) => (
                        <ImageListItem key={idx}>
                          <img
                            src={pht}
                            alt={`Task ${tsk.id} photo ${idx + 1}`}
                            loading="lazy"
                            style={{ objectFit: "cover", borderRadius: 4 }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  )}
                </MuiCard>
              </Grid>
            ))}
          </Grid>
        </MuiCard>
      </Slide>
    );
  };

  // Breadcrumb configuration
  const breadcrumbs = [
    { label: "Tasks", path: "/tasks-list" },
    { label: "Production Tasks" },
  ];

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth={false}>
        {/* Page Header */}
        <MuiPageHeader
          title="Production Tasks by Order"
          subtitle={`${filteredOrders.length} orders found`}
          breadcrumbs={breadcrumbs}
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleRefresh}
                sx={{ color: "text.secondary" }}
              >
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                color="warning"
                startIcon={<LocalShippingIcon />}
                onClick={() => navigate("/packaging")}
                sx={{ borderRadius: 2 }}
              >
                TO PACKAGE
              </Button>
            </Box>
          }
        />

        {/* Search Section */}
        <MuiCard sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TextField
              size="small"
              placeholder="Search orders, customer, label, date, status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300, flex: 1, maxWidth: 450 }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Chip
                icon={<FactoryIcon />}
                label={`${filteredOrders.length} / ${baseOrders.length} orders`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        </MuiCard>

        {/* DataGrid */}
        <MuiCard noPadding>
          {loading && filteredOrders.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 500,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredOrders}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedOrder(params.row)}
              sx={{
                border: 0,
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "action.hover",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "primary.50",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "grey.50",
                  borderBottom: 2,
                  borderColor: "divider",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: 1,
                  borderColor: "divider",
                },
                minHeight: 500,
              }}
            />
          )}
        </MuiCard>

        {/* Task Details Panel */}
        {renderTaskDetailsPanel()}
      </Container>
    </Box>
  );
};

MuiTasksList.propTypes = {
  setsectionTasks: PropTypes.func,
  sectiontasks: PropTypes.array,
  refresh: PropTypes.func,
  type: PropTypes.string,
  allTasks: PropTypes.any,
  setallTasks: PropTypes.any,
  openDrawer: PropTypes.any,
  setopenDrawer: PropTypes.any,
  selectedReplace: PropTypes.any,
};

export default MuiTasksList;
